import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Download, MessageCircle, Send } from 'lucide-react';
import type { Quotation } from '@tailoring-crm/shared';
import { formatCurrency, formatDate } from '../../lib/format.js';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tailoring-crm/ui';
import { QuotationStateBadge } from './quotation-state-badge.js';
import { useQuotationActions } from './use-quotation-actions.js';

/**
 * One quotation, as both screens that list them draw it.
 *
 * WHICH ACTIONS APPEAR IS DECIDED BY THE SERVER'S STATE, never by this component reading the
 * dates for itself. A draft may be edited, sent or thrown away; a sent quotation may be shared,
 * accepted, declined or revised; anything superseded is history and offers only the file. A
 * LAPSED one still offers the endings — a shop that agrees to honour an expired price is making a
 * decision it is entitled to make, and the API allows it, so hiding the button would only mean
 * somebody retyping the whole quotation to say yes.
 *
 * The row is deliberately identical on the customer's card and on the quotations list. The only thing
 * that differs is whose name is on it: on a customer's own card the name is already at the top of
 * the page, so `showCustomer` leaves it off rather than printing it twice.
 */
export interface QuotationRowProps {
  quotation: Quotation;
  /** Show the customer's name as a link. Off on the customer's own page, where it would repeat. */
  showCustomer?: boolean;
  onEdit: (quotation: Quotation) => void;
  onRevise: (quotation: Quotation) => void;
  onReject: (quotation: Quotation) => void;
}

export function QuotationRow({
  quotation,
  showCustomer = false,
  onEdit,
  onRevise,
  onReject,
}: QuotationRowProps): ReactElement {
  const { t } = useTranslation();
  const actions = useQuotationActions();

  // A lapsed price is still live in the sense that matters here: it can still be agreed to.
  const live = quotation.state === 'sent' || quotation.state === 'expired';

  return (
    <li
      data-testid="quotation-row"
      className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-border p-3 text-sm"
    >
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 font-medium">
          {/* A draft has no number yet, on purpose: the series is drawn at send, so a draft that
              is thrown away can never leave a gap in it. Nothing stands in for the missing
              number — the badge beside it already says Draft, and a placeholder says it twice. */}
          {quotation.number ? <span>{quotation.number}</span> : null}
          <span className="tabular-nums">
            {formatCurrency(quotation.totals?.totalPaise ?? quotation.preTaxTotalPaise)}
          </span>
          <QuotationStateBadge state={quotation.state} />
        </p>

        {showCustomer ? (
          <p>
            {/* Straight to the customer, which is where the conversation and the history are.
                A quotation read off a list nearly always raises the question "what else has been
                said to them?", and that is one click rather than a search. A legacy quotation
                with no customer shows the name alone. `leadName` is the frozen name field — see
                `quotationSchema`. */}
            {quotation.customerId ? (
              <Link
                to={`/customers/${quotation.customerId}`}
                className="font-medium hover:underline"
              >
                {quotation.leadName}
              </Link>
            ) : (
              <span className="font-medium">{quotation.leadName}</span>
            )}
          </p>
        ) : null}

        <p className="text-xs text-muted-foreground">
          {quotation.version > 1
            ? `${t('quotations.versionLabel', { version: quotation.version })} · `
            : ''}
          {quotation.sentAt
            ? t('quotations.sentOn', { date: formatDate(quotation.sentAt) })
            : t('quotations.startedOn', { date: formatDate(quotation.createdAt) })}
          {quotation.createdByName ? ` · ${quotation.createdByName}` : ''}
          {quotation.sentOnWhatsappAt ? ` · ${t('quotations.whatsappBadge')}` : ''}
        </p>

        {/* When the price lapses, in words on the row rather than left to the badge — "lapsed"
            tells a shop what happened but not the date it will be arguing about with a customer
            holding the paper. */}
        {quotation.validUntil ? (
          <p className="text-xs text-muted-foreground">
            {t('quotations.validUntil', { date: formatDate(quotation.validUntil) })}
          </p>
        ) : null}

        {quotation.totals && quotation.registered ? (
          <p className="text-xs text-muted-foreground">
            {t('quotations.taxLine', {
              taxable: formatCurrency(quotation.totals.taxablePaise),
              tax: formatCurrency(quotation.totals.taxPaise),
            })}
          </p>
        ) : null}

        {quotation.rejectReason ? (
          <p className="text-xs text-muted-foreground">
            {t('quotations.rejectedBecause', { reason: quotation.rejectReason })}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-1">
        {quotation.state === 'draft' ? (
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={actions.busy}
              onClick={() => onEdit(quotation)}
            >
              {t('common.edit')}
            </Button>
            <Button size="sm" disabled={actions.busy} onClick={() => actions.send(quotation)}>
              <Send aria-hidden="true" />
              {t('quotations.send.action')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={actions.busy}
              onClick={() => actions.discard(quotation)}
            >
              {t('common.delete')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('quotations.downloadFor', { number: quotation.number ?? '' })}
              disabled={actions.downloading}
              onClick={() => actions.open(quotation)}
            >
              <Download aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('quotations.whatsappFor', { number: quotation.number ?? '' })}
              disabled={actions.busy}
              onClick={() => actions.whatsapp(quotation)}
            >
              <MessageCircle aria-hidden="true" />
            </Button>

            {/* The three endings, behind one menu: agreed, declined, or renegotiated. */}
            {live ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={actions.busy}>
                    {t('quotations.decide')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => actions.accept(quotation)}>
                    {t('quotations.accept.action')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onReject(quotation)}>
                    {t('quotations.reject.action')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onRevise(quotation)}>
                    {t('quotations.revise')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}

            {/* A declined price is still a price the customer may come back to. */}
            {quotation.state === 'rejected' ? (
              <Button
                variant="outline"
                size="sm"
                disabled={actions.busy}
                onClick={() => onRevise(quotation)}
              >
                {t('quotations.revise')}
              </Button>
            ) : null}
          </>
        )}
      </div>
    </li>
  );
}

export default QuotationRow;
